import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {MvsCrudService} from "features/core/shared/service/crud/mvs-crud.service";
import {
    ProvisionCalculatedAgentResetPreviewDto,
    ProvisionCalculatedAgentResetRequestDto,
    ProvisionCalculatedAgentResetResultDto
} from "../../model/dto/api/provision-calculated-agent-reset.dto";

/**
 * Custom API service for the calculated-agent reset endpoint pair on cp.ProvisionContract (D-5,
 * FD-7) — the operator action that releases ONE event row for a fresh derivation of its calculated
 * agents by clearing `calculatedAgentsResolved`.
 *
 * Base URL `/mvsa/cp/provisionContracts` — identical to the route the alias `cp.ProvisionContract`
 * carries in cp.entity-provider.ts. The generic CRUD of that alias keeps running through the
 * registry; this service adds only the two action endpoints, exactly as
 * CpProvisionContractAgentService does for its three action dialogs.
 *
 * SCOPE IS ONE EVENT ROW PER CALL — deliberately not a selection and not a whole provision type.
 * Every money-moving operator action in cp is row-scoped, and mass processing arrives instead
 * through the CR-033 config-check worklist, which keeps guard and preview per row.
 *
 * Both endpoints run under um right `cp_agentChange` (AX-08 — no new right) PLUS the object right
 * `write` on ProvisionContract. The preview carries the same right as the action on purpose.
 *
 * REFUSAL CHANNEL — different from the sibling changeAgent service: a blocked reset is NOT an
 * HTTP 200 with `success: false`. It arrives as HTTP 400 carrying the German refusal text
 * (`ProvisionCheckRefusedException` rendered by CpControllerExceptionAdvice), so callers handle it
 * in the error branch. {@link previewReset} is the way to see the block BEFORE pressing: it returns
 * `resettable = false` plus the same reasons in `blockedBy`.
 */
@Injectable({
    providedIn: 'root'
})
export class CpProvisionCalculatedAgentResetService extends MvsCrudService {

    constructor(protected override http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/cp/provisionContracts');
    }

    /**
     * GET /{id}/resetCalculatedAgents/preview — read-only; writes nothing. Returns the rules that
     * would fire, the existing rows the current configuration no longer produces (display only,
     * AB-8) and, when the reset is blocked, the German reasons.
     */
    public previewReset(provisionContractId: number): Observable<ProvisionCalculatedAgentResetPreviewDto> {
        return this.http.get<ProvisionCalculatedAgentResetPreviewDto>(
            this.apiUrl + `/${provisionContractId}/resetCalculatedAgents/preview`
        );
    }

    /**
     * POST /{id}/resetCalculatedAgents — clears `calculatedAgentsResolved` on this one event row.
     * `agentsResolved` is not touched and no existing row is removed; the next calculation run
     * adds only what is genuinely new. Idempotent: a row that is already released answers with
     * `released = false`. The reason is mandatory (max. 255 characters) and is logged as INFO
     * M-046 together with the acting user.
     */
    public reset(provisionContractId: number, request: ProvisionCalculatedAgentResetRequestDto): Observable<ProvisionCalculatedAgentResetResultDto> {
        return this.http.post<ProvisionCalculatedAgentResetResultDto>(
            this.apiUrl + `/${provisionContractId}/resetCalculatedAgents`, request
        );
    }

}
